import { dateLib } from "@agentcall/core/lib"
import { useState } from "react"
import { ArrowLeft } from "react-feather"
import { useNavigate, useSearchParams } from "react-router-dom"
import { type OperationRuleModels, operationRuleApi } from "@/api/client/operation-rule"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label/label"
import { OperationalRuleSnapshotView } from "../OperationalRuleSnapshotView"

type OperationRuleLogItem = OperationRuleModels.OperationRuleList200DefaultRuleOperationRuleLogsItem

// 運用ルール履歴ページの URL。propertyId 無し=オーナー既定、あり=物件固有(E-13)。
export const operationalRuleHistoryPath = (propertyId: number | null) =>
  `/profile/operational-rule/history${propertyId == null ? "" : `?propertyId=${propertyId}`}`

export function OperationalRuleHistoryPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const propertyIdParam = searchParams.get("propertyId")
  const propertyId = propertyIdParam ? Number(propertyIdParam) : null

  const [selectedLog, setSelectedLog] = useState<OperationRuleLogItem | null>(null)

  // 一覧 API に履歴を同梱して取得する(このページだけ withOperationRuleLogs=true)。
  const { data, isLoading } = operationRuleApi.useOperationRuleList({
    withOperationRuleLogs: "true",
  })

  const scope =
    propertyId == null
      ? { label: "既定（すべての物件に適用）", rule: data?.defaultRule ?? null }
      : (() => {
          const item = data?.propertyRules.find((rule) => rule.propertyId === propertyId)
          return {
            label: item?.propertyName ?? `物件ID: ${propertyId}`,
            rule: item?.operationRule ?? null,
          }
        })()

  // 変更履歴は API が新しい順で返す。
  const logs: OperationRuleLogItem[] = scope.rule?.operationRuleLogs ?? []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <span className="text-sm text-slate-500">読み込み中...</span>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <Button variant="outline" size="sm" onClick={() => navigate("/profile/operational-rule")}>
          <ArrowLeft className="size-4" />
          戻る
        </Button>
        <h1 className="text-base font-semibold text-primary">運用ルール履歴</h1>
      </div>

      <Card>
        <CardContent className="px-4 py-4 space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-semibold text-slate-600">対象</Label>
            <span className="text-sm text-slate-700">{scope.label}</span>
            {propertyId != null && (
              <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600">
                個別
              </span>
            )}
          </div>
          <Label className="text-sm font-semibold text-slate-600">現在の設定</Label>
          {scope.rule ? (
            <OperationalRuleSnapshotView rule={scope.rule} />
          ) : (
            <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-500">
              運用ルールは設定されていません
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="px-4 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold text-slate-600">変更履歴</Label>
            <span className="text-xs text-slate-400">{logs.length}件（新しい順）</span>
          </div>
          {logs.length === 0 ? (
            <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-500">
              変更履歴はありません
            </p>
          ) : (
            <div className="divide-y divide-slate-100 rounded-md border">
              {logs.map((log) => (
                <div key={log.id} className="flex items-center justify-between gap-2 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-700">
                      {dateLib.formatDateTime(log.createdAt)}
                      {log.deleted && (
                        <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600">
                          削除
                        </span>
                      )}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedLog(log)}
                  >
                    詳細
                  </Button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-400">
            保存するたびに、その時点の設定内容が記録されます。
          </p>
        </CardContent>
      </Card>

      {/* 履歴1件のスナップショット */}
      <Dialog open={selectedLog !== null} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedLog?.deleted ? "削除時点の設定" : "変更時点の設定"}</DialogTitle>
            <DialogDescription>
              {selectedLog ? dateLib.formatDateTime(selectedLog.createdAt) : ""}
            </DialogDescription>
          </DialogHeader>
          {selectedLog && <OperationalRuleSnapshotView rule={selectedLog} />}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSelectedLog(null)}>
              閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
